import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FiAlertCircle,
  FiBookOpen,
  FiChevronLeft,
  FiChevronRight,
  FiClipboard,
  FiClock,
  FiFileText,
  FiHome,
  FiLoader,
  FiPaperclip,
  FiRefreshCw,
} from 'react-icons/fi';
import { fetchEssayHistory, fetchEssayHistoryDetail } from '../services/essayApi';
import EssayResult from './EssayResult';
import MathMarkdown from './MathMarkdown';
import ModelAnswerPanel from './ModelAnswerPanel';

const fallbackSubject = {
  id: 'integrated',
  name: '통합 교과',
  icon: FiFileText,
  color: '#2877df',
  softColor: '#eaf3ff',
};

export function formatHistoryDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '날짜 정보 없음';
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

function getHistoryTypeLabel(item) {
  if (item?.taskType === 'solve') return '문제 풀이';
  return item?.assessmentType === 'essay' ? '논술 첨삭' : '서술형 첨삭';
}

function getModelAnswerLabel(item) {
  if (item?.taskType === 'solve') return 'AI 단계별 풀이';
  return item?.assessmentType === 'essay' ? '논술 모범답안' : '서술형 모범답안';
}

function isAttachmentUsed(value) {
  return value === true || value === 1 || value === '1' || value === 'Y';
}

function hasHistoryAttachment(history) {
  return isAttachmentUsed(history?.hasAttachments ?? history?.usedAttachment);
}

function HistoryStatus({ type, message, onRetry, onStart, view = 'history' }) {
  const isModelAnswerView = view === 'modelAnswers';
  if (type === 'loading') {
    return (
      <div className="ai-essay-history-status" role="status" aria-live="polite">
        <FiLoader className="is-spinning" aria-hidden="true" />
        <strong>{isModelAnswerView ? '모범답안을 불러오고 있습니다.' : '첨삭 내역을 불러오고 있습니다.'}</strong>
      </div>
    );
  }

  if (type === 'error') {
    return (
      <div className="ai-essay-history-status is-error" role="alert">
        <FiAlertCircle aria-hidden="true" />
        <strong>{isModelAnswerView ? '모범답안을 불러오지 못했습니다.' : '첨삭 내역을 불러오지 못했습니다.'}</strong>
        <p>{message}</p>
        <button type="button" onClick={onRetry}><FiRefreshCw aria-hidden="true" />다시 시도</button>
      </div>
    );
  }

  return (
    <div className="ai-essay-history-status is-empty">
      {isModelAnswerView ? <FiBookOpen aria-hidden="true" /> : <FiClipboard aria-hidden="true" />}
      <strong>{isModelAnswerView ? '저장된 모범답안이 없습니다.' : '저장된 첨삭 내역이 없습니다.'}</strong>
      <p>{isModelAnswerView
        ? '답안 첨삭이나 문제 풀이를 완료하면 생성된 모범답안을 이곳에서 확인할 수 있습니다.'
        : '답안 진단이나 문제 풀이를 완료하면 이곳에서 다시 확인할 수 있습니다.'}</p>
      <button type="button" onClick={onStart}><FiFileText aria-hidden="true" />첨삭 시작하기</button>
    </div>
  );
}

function EssayHistory({ subjects, onHome, view = 'history' }) {
  const isModelAnswerView = view === 'modelAnswers';
  const [items, setItems] = useState([]);
  const [listStatus, setListStatus] = useState('loading');
  const [listError, setListError] = useState('');
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [detailStatus, setDetailStatus] = useState('idle');
  const [detailError, setDetailError] = useState('');
  const listRequestRef = useRef(0);
  const detailRequestRef = useRef(0);

  const getSubject = useCallback((subjectId) => (
    subjects.find((subject) => subject.id === subjectId) || fallbackSubject
  ), [subjects]);

  const scrollToTop = () => {
    document.querySelector('.ai-essay-page')?.scrollTo({ top: 0, behavior: 'auto' });
  };

  const loadHistory = useCallback(async () => {
    const requestId = listRequestRef.current + 1;
    listRequestRef.current = requestId;
    setListStatus('loading');
    setListError('');

    try {
      const historyItems = await fetchEssayHistory();
      if (listRequestRef.current !== requestId) return;
      setItems(historyItems.filter((item) => item && item.historyId));
      setListStatus('success');
    } catch (error) {
      if (listRequestRef.current !== requestId) return;
      setItems([]);
      setListError(error?.message || '잠시 후 다시 시도해 주세요.');
      setListStatus('error');
    }
  }, []);

  useEffect(() => {
    loadHistory();
    return () => {
      listRequestRef.current += 1;
      detailRequestRef.current += 1;
    };
  }, [loadHistory]);

  const openDetail = async (item) => {
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    setSelectedSummary(item);
    setSelectedHistory(null);
    setDetailStatus('loading');
    setDetailError('');
    scrollToTop();

    try {
      const history = await fetchEssayHistoryDetail(item.historyId);
      if (detailRequestRef.current !== requestId) return;
      setSelectedHistory(history);
      setDetailStatus('success');
    } catch (error) {
      if (detailRequestRef.current !== requestId) return;
      setDetailError(error?.message || '잠시 후 다시 시도해 주세요.');
      setDetailStatus('error');
    }
  };

  const closeDetail = () => {
    detailRequestRef.current += 1;
    setSelectedSummary(null);
    setSelectedHistory(null);
    setDetailStatus('idle');
    setDetailError('');
    scrollToTop();
  };

  const retryDetail = () => {
    if (selectedSummary) openDetail(selectedSummary);
  };

  const detailSubject = getSubject(selectedHistory?.subject || selectedSummary?.subject);
  const SubjectIcon = detailSubject.icon;
  const isDetailOpen = Boolean(selectedSummary);

  return (
    <div
      className="ai-essay-detail ai-essay-history-screen"
      style={{
        '--essay-accent': isDetailOpen ? detailSubject.color : '#2877df',
        '--essay-soft': isDetailOpen ? detailSubject.softColor : '#eaf3ff',
      }}
    >
      <header className="ai-essay-detail-header">
        <button
          type="button"
          onClick={isDetailOpen ? closeDetail : onHome}
          aria-label={isDetailOpen
            ? `${isModelAnswerView ? '모범답안 목록' : '첨삭 내역'}으로 돌아가기`
            : 'AI 서술형 및 논술 첨삭 홈'}
        >
          <FiChevronLeft aria-hidden="true" />
        </button>
        <h1>{isDetailOpen ? detailSubject.name : (isModelAnswerView ? '모범답안' : '첨삭 내역')}</h1>
        <button
          type="button"
          onClick={isDetailOpen ? onHome : loadHistory}
          aria-label={isDetailOpen
            ? 'AI 서술형 및 논술 첨삭 홈'
            : `${isModelAnswerView ? '모범답안' : '첨삭 내역'} 새로고침`}
          disabled={!isDetailOpen && listStatus === 'loading'}
        >
          {isDetailOpen ? <FiHome aria-hidden="true" /> : <FiRefreshCw aria-hidden="true" />}
        </button>
      </header>

      <div className="ai-essay-detail-content ai-essay-history-content">
        {isDetailOpen ? (
          <>
            {detailStatus === 'loading' && (
              <div className="ai-essay-history-status" role="status" aria-live="polite">
                <FiLoader className="is-spinning" aria-hidden="true" />
                <strong>{isModelAnswerView ? '모범답안을 불러오고 있습니다.' : '첨삭 상세 내용을 불러오고 있습니다.'}</strong>
              </div>
            )}
            {detailStatus === 'error' && (
              <HistoryStatus type="error" message={detailError} onRetry={retryDetail} view={view} />
            )}
            {detailStatus === 'success' && selectedHistory?.result && (
              <>
                <div className="ai-essay-history-detail-heading">
                  <span aria-hidden="true"><SubjectIcon /></span>
                  <div>
                    <strong>{isModelAnswerView ? getModelAnswerLabel(selectedHistory) : getHistoryTypeLabel(selectedHistory)}</strong>
                    <small><FiClock aria-hidden="true" />{formatHistoryDate(selectedHistory.createdAt)}</small>
                  </div>
                </div>
                {isModelAnswerView ? (
                  <div className="ai-essay-model-answer-detail">
                    <section className="ai-essay-result-panel ai-essay-model-answer-problem">
                      <header><span><FiFileText aria-hidden="true" /></span><div><h2>문제 확인</h2></div></header>
                      <MathMarkdown
                        value={selectedHistory.result.detected?.problem
                          || selectedHistory.problemText
                          || selectedSummary?.problemPreview
                          || '저장된 문제 내용이 없습니다.'}
                      />
                    </section>
                    <ModelAnswerPanel
                      title={getModelAnswerLabel(selectedHistory)}
                      value={selectedHistory.result.modelAnswer}
                      emptyMessage="이 내역에는 저장된 모범답안이 없습니다."
                    />
                    <div className="ai-essay-result-actions">
                      <button type="button" className="is-primary is-history-back" onClick={closeDetail}>
                        <FiChevronLeft aria-hidden="true" />모범답안 목록으로
                      </button>
                    </div>
                  </div>
                ) : (
                  <EssayResult
                    result={selectedHistory.result}
                    meta={{
                      ...selectedHistory,
                      usedAttachment: hasHistoryAttachment(selectedHistory),
                      historySaved: true,
                    }}
                    subject={detailSubject}
                    historyMode
                    onHistoryBack={closeDetail}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <section className="ai-essay-history" aria-labelledby="ai-essay-history-title">
            <div className="ai-essay-history__heading">
              <div>
                <h2 id="ai-essay-history-title">{isModelAnswerView ? '내 모범답안' : '내 첨삭 내역'}</h2>
                <p>{isModelAnswerView
                  ? '완료한 답안 첨삭과 문제 풀이에서 생성된 모범답안을 확인할 수 있습니다.'
                  : '완료한 답안 첨삭과 문제 풀이를 다시 확인할 수 있습니다.'}</p>
              </div>
              {listStatus === 'success' && items.length > 0 && <strong>{items.length}건</strong>}
            </div>

            {listStatus === 'loading' && <HistoryStatus type="loading" view={view} />}
            {listStatus === 'error' && (
              <HistoryStatus type="error" message={listError} onRetry={loadHistory} view={view} />
            )}
            {listStatus === 'success' && items.length === 0 && (
              <HistoryStatus type="empty" onStart={onHome} view={view} />
            )}

            {listStatus === 'success' && items.length > 0 && (
              <div className="ai-essay-history-list">
                {items.map((item) => {
                  const itemSubject = getSubject(item.subject);
                  const ItemSubjectIcon = itemSubject.icon;
                  const isSolveMode = item.taskType === 'solve';
                  return (
                    <button
                      key={item.historyId}
                      type="button"
                      className="ai-essay-history-card"
                      style={{ '--history-color': itemSubject.color, '--history-soft': itemSubject.softColor }}
                      onClick={() => openDetail(item)}
                      aria-label={`${itemSubject.name} ${isModelAnswerView ? getModelAnswerLabel(item) : getHistoryTypeLabel(item)} ${isModelAnswerView ? '보기' : '상세 보기'}`}
                    >
                      <span className="ai-essay-history-card__icon" aria-hidden="true"><ItemSubjectIcon /></span>
                      <div className="ai-essay-history-card__body">
                        <div className="ai-essay-history-card__meta">
                          <strong>{itemSubject.name} · {isModelAnswerView ? getModelAnswerLabel(item) : getHistoryTypeLabel(item)}</strong>
                          <small><FiClock aria-hidden="true" />{formatHistoryDate(item.createdAt)}</small>
                        </div>
                        <div className="ai-essay-history-card__problem">
                          <MathMarkdown value={String(item.problemPreview || '').trim() || '문제 내용을 확인해 주세요.'} />
                        </div>
                        {!isModelAnswerView && item.summary && (
                          <div className="ai-essay-history-card__summary">
                            <MathMarkdown value={item.summary} />
                          </div>
                        )}
                        <div className="ai-essay-history-card__footer">
                          <span>{isModelAnswerView
                            ? getModelAnswerLabel(item)
                            : (isSolveMode ? '단계별 풀이' : `종합 ${Math.max(0, Math.min(100, Number(item.overallScore) || 0))}점`)}</span>
                          {hasHistoryAttachment(item) && <span><FiPaperclip aria-hidden="true" />첨부 자료</span>}
                        </div>
                      </div>
                      <FiChevronRight className="ai-essay-history-card__arrow" aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default EssayHistory;
